// Shared alert-normalisation and filtering logic used by both App.jsx
// (mobile) and DesktopApp.jsx (desktop) shells.

// Normalise a raw alert from the API into the shape the UI expects.
// Backend may use snake_case; UI uses camelCase.
export function normaliseAlert(raw) {
  return {
    txHash:     raw.tx_hash    ?? raw.txHash,
    trader:     raw.trader     ?? raw.address,
    username:   raw.username   ?? null,
    rank:       raw.rank       ?? null,
    winRate:    raw.win_rate   ?? raw.winRate   ?? null,
    resolved:   raw.resolved   ?? null,
    side:       raw.side?.toUpperCase() ?? "BUY",
    outcome:    raw.outcome?.toUpperCase() ?? "YES",
    market:     raw.market     ?? raw.market_question ?? "",
    slug:       raw.slug       ?? raw.market_slug     ?? null,
    // eventSlug — fallback only, used when slug is missing. See marketUrl()
    // in format.js for why slug (per-market) is preferred for the deep link.
    eventSlug:  raw.event_slug ?? raw.eventSlug        ?? null,
    usd:        raw.usd        ?? raw.size_usd        ?? 0,
    price:      raw.price      ?? null,
    conviction: (raw.conviction ?? raw.conviction_multiplier) >= 1
                  ? (raw.conviction ?? raw.conviction_multiplier)
                  : null,
    ts:         raw.ts         ?? raw.timestamp_ms ?? (raw.timestamp ? Date.parse(raw.timestamp) : Date.now()),
    potentialPayout:  raw.potential_payout   ?? raw.potentialPayout  ?? null,
    potentialProfit:  raw.potential_profit   ?? raw.potentialProfit  ?? null,
    impliedReturnPct: raw.implied_return_pct ?? raw.impliedReturnPct ?? null,
    weekRank:  raw.week_rank  ?? raw.weekRank  ?? null,
    monthRank: raw.month_rank ?? raw.monthRank ?? null,
    allRank:   raw.all_rank   ?? raw.allRank   ?? null,
    dormantDays: raw.dormant_days ?? raw.dormantDays ?? null,
    categories:  raw.categories   ?? [],
  };
}

export function normaliseTrader(raw) {
  return {
    id:        raw.address     ?? raw.id,
    username:  raw.username    ?? null,
    rank:      raw.rank        ?? 0,
    // pnlPeriod — PnL for whichever leaderboard window this trader was
    // fetched under (what earned them this rank). Use for row display.
    pnlPeriod: raw.pnl_period  ?? raw.pnlPeriod ?? 0,
    // volPeriod — raw trading volume for that same window. Explains why
    // pnlPeriod can far exceed a trader's current open-trades value.
    volPeriod: raw.vol_period  ?? raw.volPeriod ?? 0,
    // pnlDay/pnlAll — real day/all-time PnL for the trader-profile stats,
    // null (not 0) when the backend didn't have that period's figure.
    pnlDay:    raw.pnl_day     ?? raw.pnlDay    ?? null,
    pnlAll:    raw.pnl_all     ?? raw.pnlAll    ?? null,
    winRate:   raw.win_rate    ?? raw.winRate   ?? null,
    resolved:  raw.resolved    ?? 0,
  };
}

// Normalise a raw /activity trade fill (from the trader-detail trade
// history list) into the shape the UI expects. Confirmed live field names
// (2026-07-11): proxyWallet, timestamp (epoch SECONDS), conditionId, size
// (tokens), usdcSize (USD), transactionHash, price, side, title, slug,
// outcome — same endpoint/shape as normaliseAlert's source data, but this
// is fed by the on-demand /traders/{address}/activity route rather than
// the alert stream, so it's kept as its own function rather than reusing
// normaliseAlert (no rank/winRate/conviction on a raw trade fill).
export function normaliseTrade(raw) {
  return {
    txHash: raw.transactionHash ?? raw.tx_hash ?? "",
    side:   raw.side?.toUpperCase() ?? "BUY",
    outcome: raw.outcome?.toUpperCase() ?? "YES",
    market: raw.title ?? raw.market ?? "",
    slug:      raw.slug ?? null,
    eventSlug: raw.eventSlug ?? null,  // prefer this for market links — slug 404s
    price:  raw.price ?? null,
    size:   raw.size ?? 0,
    usd:    raw.usdcSize ?? raw.usd ?? 0,
    ts:     raw.timestamp != null ? raw.timestamp * 1000 : Date.now(),
  };
}

// Normalise a raw OPEN position (/traders/{address}/positions) into the
// shape the UI expects. Confirmed live fields: title, outcome, curPrice,
// slug, eventSlug, conditionId, size (SHARES, not USD — do not use for
// display), currentValue (real USD mark value), and position_size_usd
// (backend-added rename of Polymarket's own initialValue, real USD entry
// cost — see core.py's get_open_positions). MOCK_OPEN uses market/size/price
// (a different, older shape) which is why this wasn't caught until real API
// data was compared against it.
export function normalisePosition(raw) {
  return {
    conditionId:     raw.conditionId ?? raw.id ?? "",
    market:          raw.title ?? raw.market ?? "",
    slug:            raw.slug ?? null,
    eventSlug:       raw.eventSlug ?? null,
    outcome:         raw.outcome?.toUpperCase() ?? "YES",
    price:           raw.curPrice ?? raw.price ?? null,
    currentValue:    raw.currentValue ?? 0,
    positionSizeUsd: raw.position_size_usd ?? raw.positionSizeUsd ?? 0,
  };
}

// Normalise a raw CLOSED position (/traders/{address}/closed-positions).
// Confirmed live fields: title, outcome, realizedPnl, curPrice, timestamp
// (epoch SECONDS, resolution/settlement time), conditionId, avgPrice,
// totalBought (a SHARE count despite the name — see core.py's
// fetch_closed_positions docstring), and position_size_usd (backend-computed
// totalBought * avgPrice, real USD). There is NO market/pnl/won/size/at
// field on the real response — those only existed on MOCK_CLOSED, which is
// why this rendered fine against mock data but blank against the real API.
export function normaliseClosedPosition(raw) {
  return {
    conditionId:     raw.conditionId ?? raw.id ?? "",
    market:          raw.title ?? raw.market ?? "",
    slug:            raw.slug ?? null,
    eventSlug:       raw.eventSlug ?? null,
    outcome:         raw.outcome?.toUpperCase() ?? "YES",
    pnl:             raw.realizedPnl ?? raw.pnl ?? 0,
    won:             (raw.realizedPnl ?? raw.pnl ?? 0) > 0,
    positionSizeUsd: raw.position_size_usd ?? raw.positionSizeUsd ?? 0,
    at:              raw.timestamp != null ? new Date(raw.timestamp * 1000).toLocaleDateString() : (raw.at ?? "—"),
    resolvedTs:      raw.timestamp != null ? raw.timestamp * 1000 : null,
  };
}

// Normalise a single buy/sell fill from the position-timeline endpoint
// (/traders/{address}/positions/{conditionId}/trades).
export function normalisePositionTrade(raw) {
  return {
    side:  raw.side?.toUpperCase() ?? "BUY",
    price: raw.price ?? null,
    usd:   raw.usdcSize ?? raw.usd ?? 0,
    ts:    raw.timestamp != null ? raw.timestamp * 1000 : Date.now(),
  };
}

// Client-side filter pass: dedupe by txHash, rank-gate (favorites can
// bypass the rank filter via favBypassRank), then either let favorites
// bypass conviction/win-rate entirely (favBypass) or apply both thresholds.
// Sorted newest-first.
export function filterVisibleAlerts(alerts, {
  favSet, topN, favBypassRank, conviction, winFloor, favBypass, rankWindow = "day",
  whaleUsd = 0, dormancyDays = 0, excludeWhale = false, excludeDormant = false,
}) {
  const seen = new Set();
  const rankByWindow = { day: "rank", week: "weekRank", month: "monthRank", all: "allRank" };
  const rankKey = rankByWindow[rankWindow] ?? "rank";
  return alerts
    .filter((a) => {
      if (seen.has(a.txHash)) return false;
      seen.add(a.txHash);
      const isFav       = favSet.has(a.trader);
      const windowRank  = a[rankKey];
      const passesRank  = windowRank == null || windowRank <= topN || (isFav && favBypassRank);
      if (!passesRank) return false;
      if (isFav && favBypass) return true;

      const passesConv     = a.conviction == null || a.conviction >= conviction;
      const passesWhale    = whaleUsd > 0 && a.usd >= whaleUsd;
      const passesDormancy = dormancyDays > 0 && a.dormantDays != null && a.dormantDays >= dormancyDays;
      const passesSignal   = passesConv || passesWhale || passesDormancy;
      const passesWin      = a.winRate == null || a.winRate >= winFloor;
      if (!(passesSignal && passesWin)) return false;

      const cats = a.categories ?? [];
      if (excludeWhale   && cats.includes("whale"))          return false;
      if (excludeDormant && cats.includes("dormant_wallet")) return false;

      return true;
    })
    .sort((x, y) => y.ts - x.ts);
}
